import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sortWorkOrdersByPriority } from '../../../utils/work-order-occupancy';
import WorkOrderListItem from '../../work-orders/WorkOrderListItem';

const getStatusColor = (status) => {
  switch (status) {
    case 'OPEN':
      return 'bg-yellow-100 text-yellow-800';
    case 'IN_PROGRESS':
      return 'bg-blue-100 text-blue-800';
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'CANCELED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const SectionAccordion = ({
  title,
  count,
  iconColorClass,
  badgeColorClass,
  headerBgClass,
  headerHoverClass,
  isOpen,
  onToggle,
  children,
}) => {
  return (
    <div className="bg-white shadow overflow-hidden rounded-lg">
      <button
        type="button"
        className={`w-full text-left px-4 py-4 border-b border-gray-200 sm:px-6 ${headerBgClass} ${headerHoverClass} transition-colors duration-150 cursor-pointer`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className={`h-5 w-5 mr-2 ${iconColorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {title}
            </h3>
            <svg
              className={`ml-2 h-5 w-5 text-gray-400 transform transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex items-center text-sm">
            <span className={`inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full ${badgeColorClass} font-medium`}>
              {count}
            </span>
          </div>
        </div>
      </button>
      {isOpen && children}
    </div>
  );
};

export default function MyWorkOrdersColumn({
  openOrders = [],
  inProgressOrders = [],
  total = 0,
  loading = false,
  error = null,
  searchTerm = '',
  reservationsByProperty = {},
  onViewWorkOrder = () => {},
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = () => {},
}) {
  const [isOpenSectionExpanded, setIsOpenSectionExpanded] = useState(true);
  const [isInProgressSectionExpanded, setIsInProgressSectionExpanded] = useState(true);

  const isSearching = searchTerm && searchTerm.length >= 2;
  const totalLoaded = openOrders.length + inProgressOrders.length;
  const isInitialLoading = loading && totalLoaded === 0;

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center text-red-500">
        Error loading work orders: {error.message}
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        <p className="mt-2 text-gray-600">Loading your work orders...</p>
      </div>
    );
  }

  if (totalLoaded === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        <h3 className="text-base font-medium text-gray-900">
          {isSearching ? 'No matching work orders' : 'No work orders assigned to you'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {isSearching
            ? `No work orders match your search for "${searchTerm}"`
            : 'When a manager assigns you a work order, it will appear here.'}
        </p>
        <div className="mt-4">
          <Link
            to="/dashboard/work-orders"
            className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            View all work orders
            <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </Link>
        </div>
      </div>
    );
  }

  const renderItems = (orders) => {
    if (orders.length === 0) {
      return (
        <div className="p-6 text-center text-gray-500 text-sm">
          None in this section.
        </div>
      );
    }
    return (
      <div className="overflow-y-auto" style={{ maxHeight: '800px' }}>
        <ul className="divide-y divide-gray-200">
          {sortWorkOrdersByPriority(orders, reservationsByProperty).map((workOrder, index) => (
            <WorkOrderListItem
              key={workOrder.id}
              workOrder={workOrder}
              index={index}
              getStatusColor={getStatusColor}
              reservationsByProperty={reservationsByProperty}
              onViewWorkOrder={onViewWorkOrder}
              showAssignedBadge
            />
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <SectionAccordion
        title="Open"
        count={openOrders.length}
        iconColorClass="text-orange-500"
        badgeColorClass="bg-orange-100 text-orange-800"
        headerBgClass="bg-orange-50"
        headerHoverClass="hover:bg-orange-100"
        isOpen={isOpenSectionExpanded}
        onToggle={() => setIsOpenSectionExpanded((prev) => !prev)}
      >
        {renderItems(openOrders)}
      </SectionAccordion>

      <SectionAccordion
        title="In Progress"
        count={inProgressOrders.length}
        iconColorClass="text-blue-500"
        badgeColorClass="bg-blue-100 text-blue-800"
        headerBgClass="bg-blue-50"
        headerHoverClass="hover:bg-blue-100"
        isOpen={isInProgressSectionExpanded}
        onToggle={() => setIsInProgressSectionExpanded((prev) => !prev)}
      >
        {renderItems(inProgressOrders)}
      </SectionAccordion>

      {hasMore && (
        <div className="text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoadingMore ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading more...
              </>
            ) : (
              'Load More Work Orders'
            )}
          </button>
        </div>
      )}

      {totalLoaded > 0 && (
        <div className="text-center text-sm text-gray-500">
          {isSearching
            ? `Showing ${totalLoaded} work orders matching "${searchTerm}"`
            : `Showing ${totalLoaded}${total > 0 ? ` of ${total}` : ''} assigned work orders`}
        </div>
      )}
    </div>
  );
}
